import React, { MouseEventHandler, ReactNode, useCallback } from 'react';
import { Link, Stack, Typography } from '@mui/material';
import { ContainedButton } from '@shared/components';
import { SpreadsheetInput } from '@retail/components';
import { useToast } from '@retail/hooks';
import { useProductsTFunction } from '@retail/products/i18n';
import { SpreadSheetRow } from '@retail/products/types';

interface Props {
  importedFile?: File;
  isLarge?: boolean;
  onDownloadTemplate?: () => void;
  importSpreadsheet: (rows: SpreadSheetRow[], file: File) => void;
  resetImportedStates: () => void;
  renderValidation?: ReactNode;
}

export const UploadContainer = ({
  importedFile,
  isLarge,
  onDownloadTemplate,
  importSpreadsheet,
  resetImportedStates,
  renderValidation,
}: Props) => {
  const t = useProductsTFunction();
  const toast = useToast();

  const handleDownloadTemplate = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.nativeEvent.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
      try {
        onDownloadTemplate?.();
      } catch (error) {
        console.error('Error downloading the template:', error);
      }
    },
    [onDownloadTemplate]
  );

  const onRemoveFile: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      e.preventDefault();
      resetImportedStates();
    },
    [resetImportedStates]
  );

  const onRejectFile = useCallback(
    () => toast.error(t('products.uploadItems.fileType')),
    [t, toast]
  );

  return (
    <Stack gap={2} alignItems="center">
      <SpreadsheetInput
        onUpload={importSpreadsheet}
        onRejectFiles={onRejectFile}
        showUploadIcon
        sx={isLarge ? { width: 500, height: 350 } : undefined}
      >
        {({ open }) => (
          <>
            <Typography pt={1}>{t('products.uploadItems.title')}</Typography>
            <Typography pb={2} variant="body2">
              {importedFile ? importedFile.name : t('products.uploadItems.acceptedFormats')}
            </Typography>

            <Stack flexDirection="row" gap={2}>
              <ContainedButton size="small" onClick={open}>
                {importedFile ? t('products.uploadItems.change') : t('products.uploadItems.upload')}
              </ContainedButton>

              {importedFile && (
                <ContainedButton color="warning" onClick={onRemoveFile} size="small">
                  {t('products.uploadItems.removeFile')}
                </ContainedButton>
              )}
            </Stack>
            {onDownloadTemplate && (
              <Link
                mt={1}
                onClick={handleDownloadTemplate}
                variant="body2"
                whiteSpace="nowrap"
                sx={{ cursor: 'pointer' }}
              >
                {t('products.uploadItems.downloadTemplate')}
              </Link>
            )}
          </>
        )}
      </SpreadsheetInput>
      <Stack minWidth="80%" maxWidth="80%">
        {renderValidation && renderValidation}
      </Stack>
    </Stack>
  );
};
